<template>
  <div ref="selectorRef" class="view-family-selector">
    <AutoCompleteInput
      v-if="showInputRef"
      ref="inputRef"
      :placeholder="placeholderRef"
      :search="searchFamilies"
      :item-component="FamilyListItem"
      :get-component-props="(item: FamilyResult) => ({ familyId: item.value, father: item.father, mother: item.mother })"
      @focus="onInputFocus"
      @blur="onInputBlur"
      @keydown.esc="onEscapeKey"
      @result-selected="familySelected">
    </AutoCompleteInput>
    <button type="button" v-else-if="canEdit"
      class="btn btn-inline btn-link link-dark" 
      :class="{ none: !props.modelValue }"
      @click="toggleShowSearchInput">
      <template v-if="textOnly || !modelValue">
        {{ displayTextRef }}
      </template>
      <FamilyListItem v-else :family-id="modelValue" :father="fatherRef" :mother="motherRef"/>
    </button>
    <div v-else class="input-value" :class="{ none: !viewFamilyRef }">
      {{ displayTextRef }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.view-family-selector {
  min-width: 300px;

  button.btn {
    padding: 0.5rem 0;
    text-align: start;
    font-size: inherit;
    color: #333;

    &.none {
      color: #888;
    }

    &:hover {
      color: var(--bs-primary);
    }
  }

  .family-list-item {
    margin-left: 1rem;
    min-width: 350px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);

    &:hover {
      box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15);
    }
  }

  .input-value {
    margin: 0;
    padding: 0.5rem 0;

    &.none {
      color: #888;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue"
import { useViewFamilyStore } from "@/rd/ViewFamilyStore"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { livingWindowStart, ViewFamily, ViewPerson } from "@/rd/ResearchDataModel"
import { onClickOutside } from "@vueuse/core"
import { sortBy } from "lodash"
import AutoCompleteInput from "@/util/AutoCompleteInput.vue"
import FamilyListItem from "@/manage/FamilyListItem.vue"
import { LoadMode } from "@/util/AsyncData"

const props = defineProps<{
  modelValue?: string
  canEdit?: boolean
  editEmpty?: boolean
  placeholder?: string
  emptyText?: string
  recentLivingOnly?: boolean
  textOnly?: boolean
}>()

const emit = defineEmits([
  'update:modelValue',
  'inputFocusChanged',
])

defineExpose({
  showSearchInput,
  focus: () => inputRef.value?.focus(),
})

const showInputRef = ref(false)

const viewFamilyStore = useViewFamilyStore()
const viewPersonStore = useViewPersonStore()

const viewFamilyRef = computed(() => viewFamilyStore.getAsyncFamily(props.modelValue, LoadMode.EnsureLoaded)?.data)
const fatherRef = computed(() => viewPersonStore.getLoadedPerson(viewFamilyRef.value?.fatherId))
const motherRef = computed(() => viewPersonStore.getLoadedPerson(viewFamilyRef.value?.motherId))
const displayTextRef = computed(() => {
  if (viewFamilyRef.value) {
    return ViewFamily.getDisplayName(fatherRef.value, motherRef.value)
  }
  if (props.modelValue) {
    return "(deleted or not visible)"
  }
  return props.emptyText ?? "None selected"
})
const placeholderRef = computed(() => props.placeholder ?? "type a spouse's name")
const defaultShowInputRef = computed(() => !props.modelValue)

const selectorRef = ref<HTMLElement>()
const inputRef = ref<InstanceType<typeof AutoCompleteInput>>()
let stopClickOutside: (() => void) | undefined

onUnmounted(onInputBlur) // ensure clickOutside cleanup

function onInputFocus() {
  console.log('ViewFamilySelector.InputFocus')
  stopClickOutside = onClickOutside(selectorRef, e => {
    const target = e.target as HTMLElement
    if (!inputRef.value?.isDropdownElement(target)) {
      showInputRef.value = defaultShowInputRef.value
    }
  })
  emit('inputFocusChanged', true)
}

function onInputBlur() {
  console.log('ViewFamilySelector.InputBlur')
  stopClickOutside?.()
  stopClickOutside = undefined
  showInputRef.value = defaultShowInputRef.value
  emit('inputFocusChanged', false)
}

watch(() => props.modelValue, () => showInputRef.value = defaultShowInputRef.value, { immediate: true })

watch(showInputRef, async value => {
  await nextTick()
  if (!value && !inputRef.value) {
    console.log('ViewFamilySelector.InputRemoved')
    onInputBlur() // fake blur when input removed from DOM
  }
}, { immediate: true })

function onEscapeKey() {
  showInputRef.value = false
}

async function showSearchInput() {
  showInputRef.value = true
  await nextTick()
  inputRef.value?.focus()
}

async function toggleShowSearchInput() {
  if (showInputRef.value) {
    showInputRef.value = false
  }
  else {
    showInputRef.value = true
    await nextTick()
    inputRef.value?.focus()
  }
}

async function searchFamilies(query: string) {
  const names = query.replace(',', ' ').split(' ').map(n => n.trim()).filter(n => n.length > 0)
  if (!names.some(n => n.length >= 3))
    return []

  const asyncViewFamilies = await viewFamilyStore.searchByNamesAsync(names)
  let results = asyncViewFamilies.map(a => a.data!).map(vf => {
    const father = viewPersonStore.getLoadedPerson(vf.fatherId) // loaded with family
    const mother = viewPersonStore.getLoadedPerson(vf.motherId) // loaded with family
    const name = ViewFamily.getDisplayName(father, mother)
    return {
      value: vf.id!,
      name,
      father,
      mother,
    } as FamilyResult
  })
  if (props.recentLivingOnly) {
    results = results.filter(fr => {
      // allow creating child-to-parent relationships to parents born close to living window
      return fr.father?.recentlyLiving ?? fr.mother?.recentlyLiving ?? true // assume recent living if no dates
    })
  }
  return sortBy(results, "name")
}

function familySelected(item: FamilyResult) {
  showInputRef.value = false
  emit('update:modelValue', item.value)
}

interface FamilyResult {
  value: string
  father?: ViewPerson
  mother?: ViewPerson
}
</script>
